import type Map from '@/components/map/Map.vue'
import type {
  GeoJSONSource,
  LayerSpecification,
  Map as MapLibreMap,
  MapGeoJSONFeature,
  StyleSpecification,
} from 'maplibre-gl'
import { centroid, polygonToLine } from '@turf/turf'
import { CommuneLayerService } from '@/services/map/CommuneLayerService'
import MapService from '@/services/map/MapService'
import { MartinStyleService } from '@/services/map/MartinStyleService'

const COMMUNE_STYLE_ID = 'commune'

const VISIBILITY_HIDDEN = 'none' as const
const VISIBILITY_VISIBLE = 'visible' as const

const HIGHLIGHT_SOURCE_ID = 'fond-highlight'
const HIGHLIGHT_OUTLINE_LAYER_ID = 'fond-highlight-outline'
const HIGHLIGHT_LABEL_LAYER_ID = 'fond-highlight-label'
const HIGHLIGHT_OUTLINE_COLOR = '#025959'
const HIGHLIGHT_OUTLINE_WIDTH = 3

/**
 * Optional labels layer config from style.metadata.
 * When present, a symbol layer is added to show text on polygons (e.g. commune names, quartier labels).
 * Backend style (GET /api/attributs/{id}/style) can include metadata.labels for any fond layer.
 * Accepts camelCase (sourceLayer, textField, layerId) or snake_case (source_layer, text_field, layer_id).
 */
interface StyleLabelsMetadata {
  sourceLayer?: string
  source_layer?: string
  textField?: string[]
  text_field?: string[]
  layerId?: string
  layer_id?: string
}

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

const FOND_LAYER_METADATA = {
  isPersistent: true,
  layerGroup: MapService.LAYER_GROUP.FOND,
} as const

function ensureHighlightLayer(map: MapLibreMap): void {
  if (map.getSource(HIGHLIGHT_SOURCE_ID)) return
  map.addSource(HIGHLIGHT_SOURCE_ID, {
    type: 'geojson',
    data: EMPTY_GEOJSON,
  })
  const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
  if (!map.getLayer(HIGHLIGHT_OUTLINE_LAYER_ID)) {
    map.addLayer(
      {
        id: HIGHLIGHT_OUTLINE_LAYER_ID,
        type: 'line',
        source: HIGHLIGHT_SOURCE_ID,
        paint: {
          'line-color': HIGHLIGHT_OUTLINE_COLOR,
          'line-width': HIGHLIGHT_OUTLINE_WIDTH,
        },
        metadata: FOND_LAYER_METADATA,
      },
      beforeId,
    )
  }
  if (!map.getLayer(HIGHLIGHT_LABEL_LAYER_ID)) {
    map.addLayer(
      {
        id: HIGHLIGHT_LABEL_LAYER_ID,
        type: 'symbol',
        source: HIGHLIGHT_SOURCE_ID,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 14,
          'text-anchor': 'center',
          'text-transform': 'uppercase',
        },
        paint: {
          'text-color': HIGHLIGHT_OUTLINE_COLOR,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
        metadata: FOND_LAYER_METADATA,
      },
      beforeId,
    )
  }
}

export function setHighlightFeature(
  map: MapLibreMap | undefined,
  feature: MapGeoJSONFeature | undefined,
  label?: string,
): void {
  if (!map) return
  ensureHighlightLayer(map)
  const source = map.getSource(HIGHLIGHT_SOURCE_ID) as GeoJSONSource | undefined
  if (!source) return
  if (!feature?.geometry) {
    source.setData(EMPTY_GEOJSON)
    return
  }

  const geoFeature = { type: 'Feature' as const, properties: {}, geometry: feature.geometry }
  const lineFeatures: GeoJSON.Feature<GeoJSON.LineString | GeoJSON.MultiLineString>[] = []
  try {
    const lineResult = polygonToLine(geoFeature as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>)
    if (lineResult.type === 'Feature') {
      lineFeatures.push(lineResult)
    } else if (lineResult.type === 'FeatureCollection' && lineResult.features?.length) {
      lineFeatures.push(...lineResult.features)
    }
  } catch {
    source.setData(EMPTY_GEOJSON)
    return
  }

  const features: GeoJSON.Feature[] = []
  for (const lineFeature of lineFeatures) {
    if (lineFeature?.geometry) {
      features.push({ type: 'Feature', properties: {}, geometry: lineFeature.geometry })
    }
  }

  if (label != null && label !== '') {
    try {
      const center = centroid(geoFeature as GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>)
      features.push({
        type: 'Feature',
        properties: { name: label.toUpperCase() },
        geometry: center.geometry,
      })
    } catch {
      // omit label if centroid fails
    }
  }

  source.setData({ type: 'FeatureCollection', features })
}

export function clearHighlight(map: MapLibreMap | undefined): void {
  if (!map) return
  const source = map.getSource(HIGHLIGHT_SOURCE_ID) as GeoJSONSource | undefined
  if (source) source.setData(EMPTY_GEOJSON)
}

/**
 * Adds an optional symbol layer for labels when style.metadata.labels is present.
 * Works for any fond style (e.g. communes, quartiers, iris) configured via backend metadata.
 */
function ensureLabelsLayerFromMetadata(
  styleId: string,
  style: StyleSpecification,
  map: MapLibreMap,
  sourceId: string,
  entry: CachedStyleEntry,
): void {
  const meta = style.metadata as { labels?: StyleLabelsMetadata } | undefined
  const labels = meta?.labels
  let sourceLayer = labels?.sourceLayer ?? labels?.source_layer
  if (!sourceLayer) {
    if (labels != null) {
      console.warn('[fondRestore] FondLayerService: style.metadata.labels missing sourceLayer/source_layer', {
        styleId,
        keys: Object.keys(labels),
      })
    }
    return
  }
  const fallbackSourceLayer = getFirstSourceLayerFromStyle(style, sourceId)
  if (sourceLayer.endsWith('_centroids') && fallbackSourceLayer) {
    sourceLayer = fallbackSourceLayer
  }

  const layerId = labels?.layerId ?? labels?.layer_id ?? `${styleId}-labels`
  if (map.getLayer(layerId)) {
    entry.layerIds = [...entry.layerIds, layerId]
    return
  }
  if (!map.getSource(sourceId)) return

  const textFieldKeys = labels?.textField ?? labels?.text_field
  const textField =
    textFieldKeys?.length
      ? (['coalesce', ...textFieldKeys.flatMap((f) => [['get', f] as const]), ''] as [string, ...unknown[]])
      : (['get', 'name'] as [string, string])
  const beforeId = MapService.getFirstIndicatorLayerId(map) ?? undefined
  map.addLayer(
    {
      id: layerId,
      type: 'symbol',
      source: sourceId,
      'source-layer': sourceLayer,
      minzoom: 0,
      layout: {
        'text-field': textField as string | import('maplibre-gl').ExpressionSpecification,
        'text-size': 11,
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-color': '#14532d',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
      metadata: FOND_LAYER_METADATA,
    },
    beforeId,
  )
  entry.layerIds = [...entry.layerIds, layerId]
}

export interface FondLayerPopupMetadata {
  title?: string
  attribute?: string
  attributes?: Array<{ label: string; attribute: string }>
}

interface CachedStyleEntry {
  sourceId: string
  layerIds: string[]
  fillLayerId: string | null
  outlineLayerId: string | null
  symbolLayerId: string | null
  popupMetadata: FondLayerPopupMetadata | null
}

const styleCache = new globalThis.Map<string, CachedStyleEntry>()

/** Returns the fond styleId that owns the given layerId, or empty string if none. Exported for use by map click handling. */
function getStyleIdByLayerId(layerId: string): string {
  const styleIds = Array.from(styleCache.keys())
  for (const styleId of styleIds) {
    const entry = styleCache.get(styleId)
    if (entry?.layerIds.includes(layerId)) return styleId
  }
  return ''
}

/** First source-layer name from style layers using the given source (e.g. 'commune'). Used when metadata.labels points to a non-working layer like commune_centroids. */
function getFirstSourceLayerFromStyle(style: StyleSpecification, sourceId: string): string | undefined {
  const layers = (style.layers ?? []) as LayerSpecification[]
  for (const layer of layers) {
    const layerSource = 'source' in layer && (layer as { source?: string }).source != null
      ? (layer as { source: string }).source
      : sourceId
    if (layerSource !== sourceId) continue
    const sl = 'source-layer' in layer ? (layer as { 'source-layer'?: string })['source-layer'] : undefined
    if (typeof sl === 'string' && sl) return sl
  }
  return undefined
}

function deriveIdsFromStyle(style: StyleSpecification): CachedStyleEntry {
  const sourceIds = Object.keys(style.sources ?? {})
  const sourceId = sourceIds[0] ?? ''
  const layers = (style.layers ?? []) as LayerSpecification[]
  const layersForSource = layers.filter((l) => {
    const layerSource =
      'source' in l && (l as { source?: string }).source != null
        ? (l as { source: string }).source
        : sourceId
    return layerSource === sourceId
  })
  const layerIds = layersForSource.map((l) => l.id)
  const fillLayer = layersForSource.find((l) => l.type === 'fill')
  const outlineLayer = layersForSource.find((l) => l.type === 'line')
  const symbolLayer = layersForSource.find((l) => l.type === 'symbol')
  const metadata = style.metadata as
    | {
        popup?: {
          title?: string
          attribute?: string
          attributes?: Array<{ label: string; attribute: string }>
        }
      }
    | undefined
  return {
    sourceId,
    layerIds,
    fillLayerId: fillLayer?.id ?? null,
    outlineLayerId: outlineLayer?.id ?? null,
    symbolLayerId: symbolLayer?.id ?? null,
    popupMetadata: metadata?.popup ?? null,
  }
}

/**
 * Loads fond layer style from GET /api/attributs/{id}/style.
 * Backend returns MapLibre v8 style with resolved tile URLs (no __MARTIN_BASE_URL__).
 */
async function loadStyle(styleId: string): Promise<StyleSpecification | null> {
  return MartinStyleService.loadStyleFromBackend(styleId)
}

/**
 * Generic service for fond (background) layers driven by GET /api/map_styles list.
 * Caches layer ids per styleId so multiple fond layers can coexist.
 */
function buildCommuneCacheEntry(): CachedStyleEntry {
  return {
    sourceId: CommuneLayerService.getSourceId(),
    layerIds: [
      CommuneLayerService.getLayerId(),
      CommuneLayerService.getOutlineLayerId(),
      CommuneLayerService.getLabelsLayerId(),
    ].filter(Boolean),
    fillLayerId: CommuneLayerService.getLayerId(),
    outlineLayerId: CommuneLayerService.getOutlineLayerId(),
    symbolLayerId: null,
    popupMetadata: null,
  }
}

export const FondLayerService = {
  async addFondLayerToMap(
    myMap: InstanceType<typeof Map> | undefined,
    styleId: string,
  ): Promise<void> {
    if (!myMap?.map) return

    const map = myMap.map
    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    if (styleId === COMMUNE_STYLE_ID) {
      await CommuneLayerService.addCommuneLayerToMap(myMap)
      const entry = buildCommuneCacheEntry()
      styleCache.set(COMMUNE_STYLE_ID, entry)
      CommuneLayerService.setCommuneLayerVisibility(myMap, true)
      ensureHighlightLayer(map)
      return
    }

    const style = await loadStyle(styleId)
    if (!style) {
      console.error('[fondRestore] FondLayerService: Could not load style', styleId)
      return
    }

    const entry = deriveIdsFromStyle(style)
    if (entry.layerIds.length === 0) {
      console.warn(
        '[FondLayerService] No layerIds derived from style (missing sources/layers?)',
        styleId,
      )
    }
    if (map.getSource(entry.sourceId)) {
      ensureLabelsLayerFromMetadata(styleId, style, map, entry.sourceId, entry)
      styleCache.set(styleId, entry)
      for (const layerId of entry.layerIds) {
        if (map.getLayer(layerId)) {
          map.setLayoutProperty(layerId, 'visibility', VISIBILITY_VISIBLE)
        }
      }
      return
    }

    const applied = await MartinStyleService.applyStyleObjectToMap(myMap, style)
    if (!applied) return

    styleCache.set(styleId, entry)
    ensureLabelsLayerFromMetadata(styleId, style, map, entry.sourceId, entry)
    for (const layerId of entry.layerIds) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', VISIBILITY_VISIBLE)
      }
    }
    ensureHighlightLayer(map)
  },

  removeFondLayerFromMap(myMap: InstanceType<typeof Map> | undefined, styleId: string): void {
    if (!myMap?.map) return

    if (styleId === COMMUNE_STYLE_ID) {
      CommuneLayerService.setCommuneLayerVisibility(myMap, false)
      return
    }

    const map = myMap.map
    const entry = styleCache.get(styleId)
    if (!entry) return

    const layerIds = [...entry.layerIds].reverse()
    for (const layerId of layerIds) {
      if (map.getLayer(layerId)) map.removeLayer(layerId)
    }
    if (map.getSource(entry.sourceId)) map.removeSource(entry.sourceId)
    styleCache.delete(styleId)
  },

  setFondLayerVisibility(
    myMap: InstanceType<typeof Map> | undefined,
    styleId: string,
    visible: boolean,
  ): void {
    if (!myMap?.map) return

    if (styleId === COMMUNE_STYLE_ID) {
      CommuneLayerService.setCommuneLayerVisibility(myMap, visible)
      return
    }

    const entry = styleCache.get(styleId)
    if (!entry) return

    const visibility = visible ? VISIBILITY_VISIBLE : VISIBILITY_HIDDEN
    const map = myMap.map
    for (const layerId of entry.layerIds) {
      if (map.getLayer(layerId)) {
        map.setLayoutProperty(layerId, 'visibility', visibility)
      }
    }
  },

  getFillLayerId(styleId: string): string {
    return styleCache.get(styleId)?.fillLayerId ?? ''
  },

  getOutlineLayerId(styleId: string): string {
    return styleCache.get(styleId)?.outlineLayerId ?? ''
  },

  /** All layer ids for this style (fill, line, symbol, etc.). Use to attach click on every layer so popup works regardless of which layer is topmost. */
  getLayerIds(styleId: string): string[] {
    return styleCache.get(styleId)?.layerIds ?? []
  },

  getStyleIdByLayerId,

  getPopupMetadata(styleId: string): FondLayerPopupMetadata | null {
    return styleCache.get(styleId)?.popupMetadata ?? null
  },

  hasLayer(styleId: string): boolean {
    return styleCache.has(styleId)
  },
}
